<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        
        <div 
            class="d-flex base-message"
            :class="deletePending ? 'delete-pending' : ''"
        >   <!-- Individual Message -->

            <div class="base-message-left text-right pr-1">
                <!-- <b-avatar :text="message.sent_by.initials" :variant="isMessageSender ? 'primary' : 'secondary'"></b-avatar> -->
                <b-avatar v-bind="avatarProps"></b-avatar>
                <!-- <b-icon icon="chat-quote" flip-h variant="primary" font-scale="1.5"></b-icon> -->
            </div>
            
            <div class="base-message-right flex-grow-1 bg-gray-100 rounded-lg d-flex flex-column justify-content-center">
                <div class="base-message-header d-flex justify-content-between align-items-start py-1 pl-3 pr-1">
                    <div class="h-100 d-flex flex-column">
                        <div class="flex-fill d-flex align-items-center">
                            <div class="d-flex align-items-baseline">
                                <div v-if="isSystemMessage" class="font-weight-600 font-size-70 text-uppercase">
                                    System Message
                                </div>
                                <div v-else class="d-flex align-items-baseline gap-2">
                                    <div 
                                        v-if="threadType === 'email'" 
                                        class="text-uppercase text-muted font-size-70 font-weight-500"
                                    >From:</div>
                                    <div class="font-weight-600">{{ sentByName}}</div>
                                </div>
                                <div class="text-muted font-size-70">
                                    <span class="mx-1">&middot;</span>
                                    {{ message.created | humanizeDateTime }}
                                </div>
                                <div
                                    v-if="isNew && !isMessageSender"
                                >
                                    <span
                                        class="font-size-60 bg-danger rounded text-white font-weight-500 ml-2" 
                                        style="padding: 2px 5px;"
                                    >NEW</span>
                                </div>
                            </div>
                        </div>
                        <div v-if="threadType === 'email' && message.tagged_tenantusers && message.tagged_tenantusers.length" class="d-flex align-items-baseline flex-wrap">
                            <div class="text-uppercase text-muted font-size-70 font-weight-500 mr-2">To:</div>
                            <div>
                                <span
                                    v-for="(notifiedTu, index) in message.tagged_tenantusers" 
                                    :key="notifiedTu.id" 
                                    class="text-dark font-weight-600 font-size-80"
                                >{{ notifiedTu.full_name }}{{ (index + 1) === message.tagged_tenantusers.length ? '' : ', ' }}</span>
                            </div>
                        </div>
                        <div v-if="threadType === 'comment' && message.tagged_tenantusers && message.tagged_tenantusers.length" class="d-flex align-items-baseline flex-wrap">
                            <div class="text-uppercase text-muted font-size-70 font-weight-500 mr-2">Tagged:</div>
                            <div>
                                <span
                                    v-for="(notifiedTu, index) in message.tagged_tenantusers" 
                                    :key="notifiedTu.id" 
                                    class="text-dark font-weight-600 font-size-80"
                                >{{ notifiedTu.full_name }}{{ (index + 1) === message.tagged_tenantusers.length ? '' : ', ' }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="d-flex align-items-center small">
                        <div class="text-muted mr-2 py-1">
                            <span class="mr-1">{{ message.created | formatDate }}</span>
                            <span>{{ message.created | formatTime }}</span>
                        </div>
                        <b-dropdown 
                            v-if="showEditDelete"
                            no-caret 
                            right
                            size="sm" 
                            variant="action-round" 
                            toggle-class="transparent"
                        >
                            <template #button-content>
                                <b-icon icon="three-dots"></b-icon>
                            </template>
                            <b-dropdown-item
                                @click="handleEditClick(message)"
                            >
                                <b-icon icon="pencil" variant="primary" class="mr-2"></b-icon>
                                Edit
                            </b-dropdown-item>
                            <b-dropdown-item
                                @click="handleDeleteClick(message)"
                            >
                                <b-icon icon="trash" variant="danger" class="mr-2"></b-icon>
                                Delete
                            </b-dropdown-item>
                        </b-dropdown>
                        <b-button
                            v-if="threadType === 'email' && !disableReplyButton"
                            size="sm"
                            variant="action-round"
                            class="transparent"
                            @click="handleReplyClick(message)"
                        >
                            <b-icon icon="reply-fill" variant="success"></b-icon>
                        </b-button>
                    </div>
                </div>
                <!-- <div class="">Subject: {{ message.subject }}</div> -->
                <div class="message-body py-2 px-3 bg-gray-50">
                    <!-- <div 
                        v-if="message.tagged_tenantusers.length > 0"
                        class="mb-2"
                    >
                        <span 
                            v-for="tu in message.tagged_tenantusers"
                            :key="tu.id"
                            class="chip chip-primary mr-2">@{{tu.full_name}}
                        </span>
                    </div> -->
                    <div class="text-show-breaks py-2">
                        <div v-html="message.body" class="ql-editor ql-editor-display"></div><!-- {{ message.body }} -->
                    </div>
                    <div v-if="message.files && message.files.length">
                        <hr class="my-2">
                        <div class="d-flex align-items-start flex-wrap gap-2">
                            <div
                                v-for="file in message.files" 
                                :key="file.id" 
                                class=""
                            >
                                <file-download-badge 
                                    :fileData="file"
                                    hide-filesize
                                    hide-download-icon
                                    button-variant="gray-primary"
                                    class="shadow"
                                ></file-download-badge>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { FormatDateHelper, FormatDateTimeLocal, FormatTimeHelper, HumanizeDateTimeHelper } from '../../js/filters-helpers';
import { currentUserInfoMixin, errorHandling } from '../../js/custom-vue-mixins';  // gives you errorToaster and errorMessageMaker
import FileDownloadBadge from '../functional-comps/FileDownloadBadge';
import { suppliosApi } from '../../js/api-service';
import { DateTime } from 'luxon';

export default {
    name: 'BaseMessage',
    components: {
        FileDownloadBadge,
    },
    mixins: [
        errorHandling,
        currentUserInfoMixin,
    ],
    props: {
        message: {
            type: Object,
            required: true,
        },
        lastReadOn: {  // ISO format date/time string for the last time TU read this message thread
            // inside this component you should compare this to this message's created timestamp, if created is later, show 'NEW' badge
            // THIS COULD BE EMPTY STRING -- we'll do that if viewer is not part of the thread (tenant admin/superadmin viewing the thread)
            type: String,
            required: false,
        },
        threadType: {  // should be 'comment' or 'email' -- used to show different things
            type: String,
            default: 'comment',
        },
        disableReplyButton: {
            type: Boolean,
            default: false,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            deletePending: false,  // Boolean to toggle special css class for when a delete is pending (clicked button, but not confirmed yet)
        }
    },
    computed: {
        // has currentUserInfo from mixin
        isMessageSender: function() {
            // returns Boolean if the current user is the sender of this message
            return !!this.message.sent_by && this.currentUserInfo.tenant_user_id === this.message.sent_by.id;
        },
        isNew: function() {  // should we show the NEW badge?  
            // we might not have lastReadOn (might be empty string) - if viewer is tenant admin/superadmin, not a member of the thread
            let msgCreated = this.message.created ? DateTime.fromISO(this.message.created) : null;
            let lastReadOn = this.lastReadOn ? DateTime.fromISO(this.lastReadOn) : null;  // in BaseMessageThread component we created a default for this, so it SHOULD always be there
            if (msgCreated && msgCreated.isValid && lastReadOn && lastReadOn.isValid) {
                return msgCreated > lastReadOn;
            }
            return false;  // return false if we didn't have valid data to make comparison
        },
        isSystemMessage: function() {
            return this.message.source === 'system';
        },
        showEditDelete: function() {
            // should we show the edit and delete dropdown?  
            // isMessageSender should be true and should not be a system message
            // ALSO FOR NOW - we will not allow external people to edit or delete... the emails would have been sent anyways
            // using new $currentUserInfo syntax
            // return this.isMessageSender && !this.isSystemMessage && this.$currentUserInfo.is_internal;
            // NOV 4 2021 -- JUST DISABLE ALL EDIT AND DELETE FOR NOW -- email messages would have been sent anyways!
            return false;
        },
        sentBy() {
            // message.sent_by might be empty, so we'll set some defaults here
            // will happen if its a system message
            return this.message.sent_by ?? {};
        },
        sentByName() {
            return this.isSystemMessage ? 'System' : (this.sentBy.full_name ?? '');
        },
        avatarProps() {
            // set text / icon / variant based on isSystemMessage and maybe other things
            // The icon prop takes precedence over the text prop.
            // If the text, src, or icon props are not provided and the default slot has no content, then the person-fill icon will be used.
            return {
                text: this.sentBy.initials,  // let it be undefined if no message.sent_by
                icon: this.isSystemMessage ? 'lightning' : undefined,
                variant: this.isMessageSender ? 'primary' : 'secondary',
            }
        },
    },
    filters: {
        formatDateTime: function(value) {
            return FormatDateTimeLocal(value);
        },
        formatDate: function(value) {
            return FormatDateHelper(value);
        },
        formatTime: function(value) {
            return FormatTimeHelper(value);
        },
        humanizeDateTime: function(value) {
            return HumanizeDateTimeHelper(value);
        },
    },
    methods: {
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        handleEditClick: function(msgObj) {
            // we won't handle edit here... instead, emit msgObj to parent, let parent handle that
            console.log(msgObj);
            this.$emit('edit', msgObj);  // pass object to parent to edit
        },
        handleReplyClick: function(msgObj) {
            // emit event to parent to open modal to create reply
            this.$emit('reply', msgObj);
        },
        handleDeleteClick: function(msgObj) {
            console.log(msgObj);
            this.deletePending = true;  // set this to toggle special CSS class
            this.$bvModal.msgBoxConfirm('Please confirm that you want to remove this message.', {
                title: 'Are you sure?',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Yes, Remove',
                cancelTitle: 'No, Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            })
            .then(value => {
                // value resolves to true if OK is clicked, false if cancel clicked, or null if modal closed
                if (value === true) {
                    console.log("From delete confirm modal, we just called performDelete");
                    return this.performDelete(msgObj);
                } else {
                    this.deletePending = false;  // toggle our css class off
                }
            })
            .catch(error => {
                this.handleError(error, 'There was an error during handleDeleteClick.');
                return false;
            })
        },
        performDelete: function(msgObj) {
            console.log("Now we would normally do performDelete on the object below");
            console.log(msgObj);
            this.loading = true;
            let msgId = msgObj.id ? msgObj.id : msgObj.pk;  // maybe we used pk instead of id?
            return suppliosApi.coreMessage.remove(msgId)  // .remove uses is_active=False 
                .then(response => {
                    console.log('removed msg via API, full response below');
                    console.log(response);
                    this.$emit('deleted', msgObj);
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while removing msg');
                    return false;
                })
                .then(status => {
                    // do something?
                    return status;
                });
                // no need for finally here... refreshing list will clear loading
        }

    }
}
</script>

<style lang="scss">

$base-message-border-color: var(--gray-200);

.base-message {

    .base-message-right {
        border: 1px solid $base-message-border-color;

        .base-message-header {
            border-bottom: 1px solid $base-message-border-color;
        }
    }

    &.delete-pending .base-message-right {
        border-color: var(--danger);
    }
}

</style>